import { Dialog, DialogContent, DialogProps, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { InformationDialogFooter } from './footer';
import { InformationDialogTitle } from './title';

type Props = {
  title?: string;
  icon?: JSX.Element;
  content: string | JSX.Element;
  onClose?: () => void;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  confirmButtonText?: string;
  confirmButtonColor?: string;
  cancelButtonText?: string;
  confirmButtonDisabled?: boolean;
} & Pick<DialogProps, 'open'>;

export const InformationDialog: FC<Props> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  confirmButtonText,
  confirmButtonColor,
  cancelButtonText,
  confirmButtonDisabled = false,
  title,
  icon,
  content
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const hasClickHandler = onConfirm || onCancel;

  const handleConfirm = async () => {
    try {
      setIsConfirming(true);
      await onConfirm?.();
    } finally {
      setIsConfirming(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  return (
    <Dialog
      PaperProps={{
        className: 'shadow-900'
      }}
      open={open}
      onClose={onClose}
      maxWidth={'sm'}
    >
      {title && <InformationDialogTitle title={title} onClose={onClose} icon={icon} />}
      <DialogContent
        dividers
        sx={{
          borderBottom: 'none',
          paddingTop: '24px',
          paddingBottom: hasClickHandler ? '8px' : '24px'
        }}
      >
        {typeof content === 'string' ? (
          <Typography paragraph marginBottom="0">
            {content}
          </Typography>
        ) : (
          content
        )}
      </DialogContent>
      {hasClickHandler && (
        <InformationDialogFooter
          onCancel={onCancel && handleCancel}
          onConfirm={onConfirm && handleConfirm}
          confirmButtonText={confirmButtonText}
          cancelButtonText={cancelButtonText}
          confirmButtonColor={confirmButtonColor}
          confirmButtonDisabled={confirmButtonDisabled}
          actionsDisabled={isConfirming}
        />
      )}
    </Dialog>
  );
};
